// Copyright (c) Ping. All rights reserved.
//
// Resolve group message / typing labels from device hex + roster maps.

import { canonicalDeviceIdHex } from './mlsBytes';

export interface GroupRosterMaps {
  readonly memberDisplayNames?: Readonly<Record<string, string>>;
  readonly memberDeviceNames?: Readonly<Record<string, string>>;
  /** sender device hex → peer account id (for avatar / directory lookup). */
  readonly memberDeviceAccounts?: Readonly<Record<string, string>>;
}

function lookupDeviceMapValue(
  senderId: string,
  map: Readonly<Record<string, string>> | undefined,
): string | undefined {
  if (map === undefined) return undefined;
  const direct = map[senderId];
  if (direct !== undefined && direct.length > 0) return direct;
  const canon = canonicalDeviceIdHex(senderId);
  if (canon !== null) {
    const fromCanon = map[canon];
    if (fromCanon !== undefined && fromCanon.length > 0) return fromCanon;
    for (const [key, value] of Object.entries(map)) {
      if (value.length === 0) continue;
      const keyCanon = canonicalDeviceIdHex(key);
      if (keyCanon === canon) return value;
    }
  }
  return undefined;
}

/** Device hex or account id → human-readable label for groups. */
export function resolveGroupSenderLabel(
  senderId: string,
  maps: GroupRosterMaps,
): string {
  const fromDevice = lookupDeviceMapValue(senderId, maps.memberDeviceNames);
  if (fromDevice !== undefined) {
    return fromDevice;
  }
  // `senderId` may already be an account id (it keys `memberDisplayNames`).
  const fromAccount = maps.memberDisplayNames?.[senderId];
  if (fromAccount !== undefined && fromAccount.length > 0) {
    return fromAccount;
  }
  // Bridge: a device-hex sender we don't have a device NAME for, but DO
  // have a device→account mapping for, can still resolve via the account
  // roster (accountId → display name). This is the common receiver case —
  // `memberDisplayNames` arrives in the group hydration while device names
  // only land once each member's `ping.profile` does.
  const bridgedAccount = lookupDeviceMapValue(senderId, maps.memberDeviceAccounts);
  if (bridgedAccount !== undefined) {
    const viaBridge = maps.memberDisplayNames?.[bridgedAccount];
    if (viaBridge !== undefined && viaBridge.length > 0) {
      return viaBridge;
    }
  }
  return senderId.length > 12 ? `…${senderId.slice(-6)}` : senderId;
}

/** True when `resolveGroupSenderLabel` could not find a roster entry. */
export function isUnresolvedGroupSenderLabel(
  senderId: string,
  label: string,
): boolean {
  if (label === senderId) return true;
  const canon = canonicalDeviceIdHex(senderId);
  if (canon !== null && label === canon) return true;
  if (senderId.length > 12 && label === `…${senderId.slice(-6)}`) return true;
  if (canon !== null && label === `…${canon.slice(-6)}`) return true;
  return false;
}

/** Resolve roster account id for a message / typing sender key. */
export function resolveGroupSenderAccountId(
  senderId: string,
  maps: GroupRosterMaps,
): string | undefined {
  const fromDevice = lookupDeviceMapValue(senderId, maps.memberDeviceAccounts);
  if (fromDevice !== undefined) return fromDevice;
  if (maps.memberDisplayNames?.[senderId] !== undefined) {
    return senderId;
  }
  return undefined;
}

export interface GroupSenderAvatar {
  readonly initials: string;
  readonly imageUri?: string;
}

/** Build avatar props from a resolved display label. */
export function groupSenderAvatarFromLabel(
  label: string,
  imageUri?: string,
): GroupSenderAvatar {
  const trimmed = label.trim();
  const initials =
    trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?';
  return imageUri !== undefined && imageUri.length > 0
    ? { initials, imageUri }
    : { initials };
}

/** Normalize roster device keys to canonical hex for persistence. */
export function normalizeGroupDeviceRosterMaps(maps: {
  readonly memberDeviceNames?: Readonly<Record<string, string>>;
  readonly memberDeviceAccounts?: Readonly<Record<string, string>>;
}): {
  memberDeviceNames?: Record<string, string>;
  memberDeviceAccounts?: Record<string, string>;
} {
  const outNames: Record<string, string> = {};
  const outAccounts: Record<string, string> = {};
  if (maps.memberDeviceNames !== undefined) {
    for (const [key, value] of Object.entries(maps.memberDeviceNames)) {
      if (value.length === 0) continue;
      const canon = canonicalDeviceIdHex(key) ?? key;
      outNames[canon] = value;
    }
  }
  if (maps.memberDeviceAccounts !== undefined) {
    for (const [key, value] of Object.entries(maps.memberDeviceAccounts)) {
      if (value.length === 0) continue;
      const canon = canonicalDeviceIdHex(key) ?? key;
      outAccounts[canon] = value;
    }
  }
  return {
    ...(Object.keys(outNames).length > 0 ? { memberDeviceNames: outNames } : {}),
    ...(Object.keys(outAccounts).length > 0
      ? { memberDeviceAccounts: outAccounts }
      : {}),
  };
}
